// src/App.jsx
import { useMemo, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';

import './index.css';

import Hero from './components/Hero';
import Footer from './components/Footer';
import LoginHandler from './components/LoginHandler';
import AdminControls from './components/AdminControls';
import Home from './components/Home';
import LineApplyButton from './components/LineApplyButton';
import PrivateRoute from './components/PrivateRoute';

import { auth } from './firebase';
import { onAuthStateChanged, signInAnonymously } from 'firebase/auth';

const queryClient = new QueryClient();
const hero = '/images/header/main.webp';

// ✅ GA4 測定ID（7a686 プロパティ）
const GA_ID = 'G-E0EYF35SF5';

// ✅ ?debug_mode=1 を付けて開く／または過去の継続セッションを検出
const isDebugSession = () => {
  try {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('debug_mode') === '1') {
      localStorage.setItem('ga_debug_mode', '1');
      return true;
    }
    return localStorage.getItem('ga_debug_mode') === '1';
  } catch {
    return false;
  }
};

// ✅ 手動 page_view（SPA対応・DebugView用フラグ付き）
const sendPageView = (path) => {
  const fire = () => {
    if (typeof window.gtag !== 'function') return false;
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
      debug_mode: isDebugSession(),
    });
    return true;
  };
  if (!fire()) setTimeout(fire, 600);
};

const AppContent = () => {
  const location = useLocation();
  const [authReady, setAuthReady] = useState(false);

  const { isAdminPage, isHomePage } = useMemo(() => {
    const path = location.pathname;
    return {
      isAdminPage: path.startsWith('/admin'),
      isHomePage: path === '/',
    };
  }, [location.pathname]);

  // ✅ ルーティングごとに手動PV送信
  useEffect(() => {
    sendPageView(location.pathname);
  }, [location.pathname]);

  // ✅ 匿名Auth（uidをlocalStorageに保持）
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        localStorage.setItem('anonUID', user.uid);
        setAuthReady(true);
      } else {
        signInAnonymously(auth)
          .then((cred) => {
            localStorage.setItem('anonUID', cred.user.uid);
            setAuthReady(true);
          })
          .catch((err) => {
            console.error('匿名ログイン失敗:', err);
            setAuthReady(true);
          });
      }
    });
    return () => unsub();
  }, []);

  if (!authReady) return <p style={{ textAlign: 'center', marginTop: 40 }}>ログイン確認中...</p>;

  return (
    <>
      {!isAdminPage && (
        <Hero
          imageUrl={hero}
          alt="キャバクラ未経験の女性モデル"
          title="未経験キャバクラ求人サイト | New-Nightlife"
          desc="未経験でも安心！LINE応募＆サポート付きで夜職デビューを応援します。"
        />
      )}

      <main style={{ width: '100%', padding: 0, margin: 0, textAlign: 'center' }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route
            path="/admin"
            element={
              <PrivateRoute>
                <AdminControls />
              </PrivateRoute>
            }
          />
          <Route path="/login" element={<LoginHandler />} />
        </Routes>
      </main>

      {isHomePage && <LineApplyButton />}
      {!isAdminPage && <Footer />}
    </>
  );
};

// ✅ GA4 スニペットを一度だけ読み込み（send_page_viewは無効）
const GAInit = () => {
  useEffect(() => {
    if (typeof window.gtag === 'function') return;

    // 同じスクリプトの重複挿入を防ぐためにidをつける
    if (!document.getElementById('ga4-tag')) {
      const s = document.createElement('script');
      s.async = true;
      s.id = 'ga4-tag';
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      document.head.appendChild(s);
    }

    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_ID, {
      anonymize_ip: true,
      send_page_view: false,
      debug_mode: isDebugSession(), // ← DebugView用
    });
  }, []);
  return null;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <GAInit />
      <Router>
        <AppContent />
      </Router>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
